import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { ReportData, LegendItem } from "src/Types/report.types";
import type { TFunction } from "i18next";
import { formatReportDate } from "src/utils/reportDataTransformer";
import { MonitoringListingSummary } from "src/Types/alerts.types";
import { AppLocale } from "src/i18n/localesConfig";
import { formatDate } from "src/i18n/format";

export type ReportContext = { t: TFunction; locale: AppLocale };

const AIRBNB_MARKER = "#f43f5e";
const VRBO_MARKER = "#f59e0b";
const NEARBY_MARKER = "#38bdf8";

// PDF styling constants
const COLORS = {
  primary: "#3b82f6",
  text: "#1e293b",
  textLight: "#64748b",
  border: "#e2e8f0",
  background: "#f8fafc",
  airbnb: AIRBNB_MARKER,
  vrbo: VRBO_MARKER,
  nearby: NEARBY_MARKER,
};

const MARGINS = {
  left: 20,
  right: 20,
  top: 20,
  bottom: 20,
};

const PAGE_WIDTH = 210; // A4 width in mm
const PAGE_HEIGHT = 297; // A4 height in mm
const CONTENT_WIDTH = PAGE_WIDTH - MARGINS.left - MARGINS.right;

/** X offset for a value drawn after a bold label, never closer than `minOffset`. */
const valueOffset = (doc: jsPDF, label: string, minOffset: number) =>
  Math.max(minOffset, doc.getTextWidth(label) + 2);

/**
 * Capture the map using a combination of the underlying map instance and html2canvas
 */
export const captureMapImage = async (mapElement: HTMLElement): Promise<string> => {
  try {
    // Wait for everything to render
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Use html2canvas on the entire container
    // Even though WebGL might not capture perfectly, we'll get the markers
    const canvas = await html2canvas(mapElement, {
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#111827",
      scale: 2,
      logging: true, // Enable logging to see what's happening
    });

    return canvas.toDataURL("image/png");
  } catch (error) {
    console.error("Error capturing map:", error);
    throw new Error("Failed to capture map image");
  }
};

/**
 * Add header to the PDF with prominent STR Monitoring branding
 */
const addHeader = (doc: jsPDF, reportData: ReportData, { t, locale }: ReportContext) => {
  const { metadata } = reportData;
  
  // Top line
  doc.setDrawColor(COLORS.primary);
  doc.setLineWidth(0.8);
  doc.line(MARGINS.left, MARGINS.top + 2, PAGE_WIDTH - MARGINS.right, MARGINS.top + 2);

  // Company branding - centered and prominent
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(COLORS.primary);
  doc.text("STR MONITORING", PAGE_WIDTH / 2, MARGINS.top + 10, { align: "center" });

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(COLORS.textLight);
  doc.text(t("report.preparedBy"), PAGE_WIDTH / 2, MARGINS.top + 16, { align: "center" });

  // Bottom line
  doc.setDrawColor(COLORS.primary);
  doc.setLineWidth(0.8);
  doc.line(MARGINS.left, MARGINS.top + 19, PAGE_WIDTH - MARGINS.right, MARGINS.top + 19);

  // Jurisdiction and Date on same line
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(COLORS.text);
  const jurisdictionText = `${t("report.jurisdiction")}:`;
  doc.text(jurisdictionText, MARGINS.left, MARGINS.top + 27);
  const jurisdictionOffset = valueOffset(doc, jurisdictionText, 25);
  doc.setFont("helvetica", "normal");
  doc.text(metadata.jurisdictionName, MARGINS.left + jurisdictionOffset, MARGINS.top + 27);

  doc.setFont("helvetica", "bold");
  const generatedText = `${t("report.generated")}:`;
  const generatedTextWidth = doc.getTextWidth(generatedText);
  doc.text(generatedText, PAGE_WIDTH - MARGINS.right - 70, MARGINS.top + 27);
  doc.setFont("helvetica", "normal");
  doc.text(
    formatReportDate(metadata.generatedDate, locale),
    PAGE_WIDTH - MARGINS.right - 70 + generatedTextWidth + 2,
    MARGINS.top + 27
  );

  // Horizontal line
  doc.setDrawColor(COLORS.border);
  doc.setLineWidth(0.3);
  doc.line(MARGINS.left, MARGINS.top + 32, PAGE_WIDTH - MARGINS.right, MARGINS.top + 32);

  return MARGINS.top + 37;
};

/**
 * Add summary statistics and map legend side by side
 */
const addSummaryAndLegend = (
  doc: jsPDF,
  reportData: ReportData,
  yPos: number,
  { t }: ReportContext
): number => {
  let currentY = yPos;
  const { statistics } = reportData;

  // Left column: Summary Statistics (width: 100mm)
  const leftColWidth = 100;

  // Title for statistics
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(COLORS.text);
  doc.text(t("dashboard.reportSummary"), MARGINS.left, currentY);

  // Title for legend (right-aligned)
  doc.text(t("dashboard.mapLegend"), PAGE_WIDTH - MARGINS.right, currentY, { align: "right" });
  currentY += 6;

  const startY = currentY;

  // LEFT SIDE: Statistics
  const stats = [
    { label: t("report.stats.total"), value: statistics.totalListings.toString() },
    { label: t("report.stats.airbnb"), value: statistics.airbnbListings.toString() },
    { label: t("report.stats.vrbo"), value: statistics.vrboListings.toString() },
    { label: t("report.stats.active"), value: statistics.activeListings.toString() },
    { label: t("report.stats.inactive"), value: statistics.inactiveListings.toString() },
    { label: t("report.stats.new30d"), value: statistics.newListings30d.toString() },
    { label: t("report.stats.inside"), value: statistics.insideBoundary.toString() },
    { label: t("report.stats.nearby"), value: statistics.nearbyBoundary.toString() },
    { label: t("report.stats.uniqueHosts"), value: statistics.uniqueHosts.toString() },
    { label: t("report.stats.multiHosts"), value: statistics.multiPropertyHosts.toString() },
    {
      label: t("report.stats.avgRate"),
      value: statistics.avgPrice ? `$${statistics.avgPrice}` : t("report.notAvailable"),
    },
  ];

  const columnWidth = leftColWidth / 2;
  const rowHeight = 5;
  let statsY = startY;

  stats.forEach((stat, index) => {
    const column = index % 2;
    const row = Math.floor(index / 2);
    const x = MARGINS.left + column * columnWidth;
    const y = statsY + row * rowHeight;

    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(COLORS.textLight);
    doc.text(stat.label + ":", x, y);
    const offset = valueOffset(doc, stat.label + ":", 38);

    doc.setFont("helvetica", "normal");
    doc.setTextColor(COLORS.text);
    doc.text(stat.value, x + offset, y);
  });

  const statsHeight = Math.ceil(stats.length / 2) * rowHeight;

  // RIGHT SIDE: Map Legend (right-aligned)
  const legendItems: LegendItem[] = [
    {
      color: COLORS.airbnb,
      label: t("dashboard.legendAirbnb"),
      shape: "circle",
      description: t("report.legend.airbnb"),
    },
    {
      color: COLORS.vrbo,
      label: t("dashboard.legendVrbo"),
      shape: "circle",
      description: t("report.legend.vrbo"),
    },
    {
      color: COLORS.nearby,
      label: t("dashboard.legendNearby"),
      shape: "circle-dashed",
      description: t("report.legend.nearby"),
    },
  ];

  if (reportData.metadata.mode === "ADDRESSES") {
    legendItems.push({
      color: COLORS.primary,
      label: t("dashboard.legendAddress"),
      shape: "pin",
      description: t("dashboard.legendAddressDesc"),
    });
  }

  if (reportData.metadata.mode === "COMMUNITY") {
    legendItems.push({
      color: "#8b5cf6",
      label: t("dashboard.legendBoundary"),
      shape: "area",
      description: t("report.legend.boundary"),
    });
  }

  let legendY = startY;
  const legendRightEdge = PAGE_WIDTH - MARGINS.right;
  
  legendItems.forEach((item) => {
    // Draw colored circle/shape (positioned from right)
    const circleX = legendRightEdge - 2;
    
    doc.setFillColor(item.color);
    if (item.shape === "circle" || item.shape === "circle-dashed") {
      doc.circle(circleX, legendY - 1, 1.5, "F");
      if (item.shape === "circle-dashed") {
        doc.setDrawColor(item.color);
        (doc as any).setLineDash([1, 1]);
        doc.circle(circleX, legendY - 1, 2, "D");
        (doc as any).setLineDash([]);
      }
    }

    // Label (right-aligned)
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(COLORS.text);
    doc.text(item.label, circleX - 4, legendY, { align: "right" });

    // Description (right-aligned, smaller)
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(COLORS.textLight);
    doc.text(item.description, circleX - 4, legendY + 3, { align: "right" });

    legendY += 6.5;
  });

  const legendHeight = legendItems.length * 6.5;

  // Return position after the taller of the two columns
  currentY = startY + Math.max(statsHeight, legendHeight) + 5;

  // Horizontal line
  doc.setDrawColor(COLORS.border);
  doc.setLineWidth(0.3);
  doc.line(MARGINS.left, currentY, PAGE_WIDTH - MARGINS.right, currentY);

  return currentY + 5;
};

/**
 * Add map image to PDF
 */
const addMapImage = (
  doc: jsPDF,
  mapImageData: string,
  yPos: number,
  { t }: ReportContext
): number => {
  let currentY = yPos;

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(COLORS.text);
  doc.text(t("report.mapTitle"), MARGINS.left, currentY);
  currentY += 6;

  // Calculate image dimensions to fit page width
  const imgWidth = CONTENT_WIDTH;
  const imgHeight = (imgWidth * 3) / 4; // Maintain aspect ratio

  // Check if we need a new page
  if (currentY + imgHeight > PAGE_HEIGHT - MARGINS.bottom) {
    doc.addPage();
    currentY = MARGINS.top;
  }

  try {
    doc.addImage(
      mapImageData,
      "PNG",
      MARGINS.left,
      currentY,
      imgWidth,
      imgHeight,
      undefined,
      "FAST"
    );
    currentY += imgHeight + 5;
  } catch (error) {
    console.error("Error adding map image:", error);
    doc.setFontSize(10);
    doc.setTextColor(COLORS.textLight);
    doc.text(t("report.mapError"), MARGINS.left, currentY);
    currentY += 10;
  }

  return currentY;
};

/**
 * Add listing details section
 */
const addListingDetails = (
  doc: jsPDF,
  reportData: ReportData,
  yPos: number,
  ctx: ReportContext
): number => {
  const { t } = ctx;
  let currentY = yPos;

  // Add new page for listings
  doc.addPage();
  currentY = MARGINS.top;

  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(COLORS.text);
  doc.text(t("dashboard.reportListings"), MARGINS.left, currentY);
  currentY += 8;

  // Group and display listings
  reportData.groups.forEach((group) => {
    // Check if we need a new page
    if (currentY > PAGE_HEIGHT - MARGINS.bottom - 40) {
      doc.addPage();
      currentY = MARGINS.top;
    }

    // Group header
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(COLORS.text);
    const groupTitle = t("report.groupTitle", {
      provider:
        group.provider === "VRBO" ? t("dashboard.providerVrbo") : t("dashboard.providerAirbnb"),
      status: group.status === "Active" ? t("report.statusActive") : t("report.statusInactive"),
      location:
        group.location === "Nearby" ? t("report.locationNearby") : t("report.locationInside"),
    });
    doc.text(groupTitle, MARGINS.left, currentY);
    const countOffset = valueOffset(doc, groupTitle, 80) + 2;
    doc.setFontSize(9);
    doc.setTextColor(COLORS.textLight);
    doc.text(t("report.listingCount", { count: group.listings.length }), MARGINS.left + countOffset, currentY);
    currentY += 6;

    // Draw each listing
    group.listings.forEach((listing) => {
      currentY = addSingleListing(doc, listing, currentY, ctx);
    });

    currentY += 3;
  });

  return currentY;
};

/**
 * Add a single listing to the PDF
 */
const addSingleListing = (
  doc: jsPDF,
  listing: MonitoringListingSummary,
  yPos: number,
  { t }: ReportContext
): number => {
  const notAvailable = t("report.notAvailable");
  let currentY = yPos;

  // Check if we need a new page
  if (currentY > PAGE_HEIGHT - MARGINS.bottom - 35) {
    doc.addPage();
    currentY = MARGINS.top;
  }

  // Background box
  doc.setFillColor(COLORS.background);
  doc.roundedRect(MARGINS.left, currentY - 2, CONTENT_WIDTH, 28, 2, 2, "F");

  // Listing name
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(COLORS.text);
  const listingName = listing.name || t("report.unnamedListing");
  doc.text(doc.splitTextToSize(listingName, CONTENT_WIDTH - 10), MARGINS.left + 2, currentY + 2);
  currentY += 6;

  // Listing details in two columns - REMOVED monitoring-specific fields
  const details = [
    { label: t("dashboard.metaListingId"), value: listing.providerListingId },
    { label: t("report.fields.roomType"), value: listing.roomType || notAvailable },
    { label: t("dashboard.metaPrice"), value: listing.price ? `${listing.price}${listing.priceQualifier ? ` ${listing.priceQualifier}` : ""}` : notAvailable },
    { label: t("dashboard.metaRating"), value: listing.ratingLabel || listing.rating || notAvailable },
    { label: t("dashboard.metaHost"), value: listing.hostName || listing.hostId || notAvailable },
    { label: t("report.fields.status"), value: listing.active ? t("report.statusActive") : t("report.statusInactive") },
    {
      label: t("report.fields.location"),
      value: `${listing.latitude.toFixed(5)}, ${listing.longitude.toFixed(5)}`,
    },
    {
      label: t("report.fields.distance"),
      value: listing.matchStatus === "NEARBY" && listing.boundaryDistanceMiles
        ? t("report.fromBoundary", { distance: listing.boundaryDistanceMiles.toFixed(2) })
        : t("dashboard.insideBoundary"),
    },
  ];

  const columnWidth = CONTENT_WIDTH / 2;
  let rowY = currentY;

  details.forEach((detail, index) => {
    const column = index % 2;
    const x = MARGINS.left + 2 + column * columnWidth;

    if (index > 0 && index % 2 === 0) {
      rowY += 4;
    }

    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(COLORS.textLight);
    doc.text(detail.label + ":", x, rowY);
    const offset = valueOffset(doc, detail.label + ":", 20);

    doc.setFont("helvetica", "normal");
    doc.setTextColor(COLORS.text);
    const valueText = doc.splitTextToSize(detail.value, columnWidth - offset - 5);
    doc.text(valueText, x + offset, rowY);
  });

  currentY = rowY + 6;

  // URL
  doc.setFontSize(7);
  doc.setTextColor(COLORS.primary);
  doc.textWithLink(t("report.viewListing"), MARGINS.left + 2, currentY, { url: listing.url });

  return currentY + 5;
};

/**
 * Add footer with page numbers and disclaimer
 */
const addFooters = (doc: jsPDF, { t, locale }: ReportContext) => {
  const pageCount = doc.getNumberOfPages();
  const disclaimer = t("report.disclaimer");
  const footerDate = formatDate(new Date(), locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    
    // Page number and report info at bottom
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(COLORS.textLight);
    
    doc.text(
      t("report.pageOf", { page: i, total: pageCount }),
      PAGE_WIDTH / 2,
      PAGE_HEIGHT - 15,
      { align: "center" }
    );
    
    doc.text(
      t("dashboard.reportTitle"),
      MARGINS.left,
      PAGE_HEIGHT - 15
    );
    
    doc.text(
      footerDate,
      PAGE_WIDTH - MARGINS.right,
      PAGE_HEIGHT - 15,
      { align: "right" }
    );

    // Disclaimer at the very bottom
    doc.setFontSize(6);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(COLORS.textLight);
    const disclaimerLines = doc.splitTextToSize(disclaimer, CONTENT_WIDTH);
    doc.text(disclaimerLines, MARGINS.left, PAGE_HEIGHT - 8);
  }
};

/**
 * Generate the complete PDF report
 */
export const generateMonitoringReport = async (
  mapElement: HTMLElement,
  reportData: ReportData,
  ctx: ReportContext
): Promise<void> => {
  try {
    // Create new PDF document
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    // Capture map image
    const mapImageData = await captureMapImage(mapElement);

    // Build the PDF
    let yPos = addHeader(doc, reportData, ctx);
    yPos = addSummaryAndLegend(doc, reportData, yPos, ctx); // Combined stats and legend
    yPos = addMapImage(doc, mapImageData, yPos, ctx);
    
    if (reportData.allListings.length > 0) {
      addListingDetails(doc, reportData, yPos, ctx);
    }

    // Add footers to all pages (includes disclaimer)
    addFooters(doc, ctx);

    // Generate filename
    const jurisdictionSlug = reportData.metadata.jurisdictionName
      .replace(/[^a-z0-9]/gi, "_")
      .replace(/_+/g, "_");
    const dateStr = new Date().toISOString().split("T")[0];
    const filename = `${ctx.t("report.filenamePrefix")}_${jurisdictionSlug}_${dateStr}.pdf`;

    // Save the PDF
    doc.save(filename);
  } catch (error) {
    console.error("Error generating PDF report:", error);
    throw error;
  }
};
